import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock, ExternalLink, Navigation } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = {
  address:
    "Shop No. 14, Commercial Block, Global City Center, Global Heights, Sector 33, Sohna, Gurugram, Haryana 122103",
  phone: "+91 92176 12215",
  hours: "8:00 AM – 10:30 PM",
  days: "Open all 7 days",
  maps_url:
    "https://www.google.com/maps/search/?api=1&query=Podi+Sambaram+Global+City+Center+Global+Heights+Sector+33+Sohna+122103",
  swiggy_url: "https://www.swiggy.com/city/gurgaon/podi-sambaram-sohna-city-rest1416415",
};

const TIME_SLOTS = [
  "8:30 AM", "9:30 AM", "10:30 AM", "12:00 PM", "1:00 PM", "2:00 PM",
  "4:00 PM", "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM", "10:00 PM",
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-[#F57C00]/60";

const Reservation = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    guests: 2,
    date: "",
    time: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, { ...form, guests: Number(form.guests) });
      toast.success("Table request received!", {
        description: `Thanks ${form.name.split(" ")[0]} — we will call ${form.phone} shortly to confirm your table for ${form.guests}.`,
      });
      setForm({ name: "", phone: "", guests: 2, date: "", time: "", notes: "" });
    } catch (err) {
      toast.error("Could not submit your request", {
        description: "Please check your details or call us directly at +91 83750 89114.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reserve" data-testid="reservation-section" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute left-0 top-1/4 h-[400px] w-[400px] rounded-full bg-[#F57C00]/8 blur-[130px]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Reservations</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-medium leading-tight tracking-tight sm:text-4xl md:text-5xl">
              Book your table,
              <span className="text-gradient-saffron italic"> the banana leaf is waiting.</span>
            </h2>
            <p className="mt-5 max-w-lg text-base text-zinc-400">
              Send us a request and we will call you back to confirm. For
              instant bookings, call us directly.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              data-testid="reservation-form"
              onSubmit={submit}
              className="card-surface mt-9 grid grid-cols-1 gap-4 rounded-3xl p-6 sm:grid-cols-2 sm:p-8"
            >
              <div>
                <label className="mb-1.5 block font-mono-accent text-[11px] uppercase tracking-widest text-zinc-500">
                  Your Name
                </label>
                <input
                  data-testid="reservation-form-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1.5 block font-mono-accent text-[11px] uppercase tracking-widest text-zinc-500">
                  Phone
                </label>
                <input
                  data-testid="reservation-form-phone-input"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="10-digit mobile"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1.5 block font-mono-accent text-[11px] uppercase tracking-widest text-zinc-500">
                  Guests
                </label>
                <select
                  data-testid="reservation-form-guests-select"
                  value={form.guests}
                  onChange={set("guests")}
                  className={`${inputCls} appearance-none`}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 15, 20].map((n) => (
                    <option key={n} value={n} className="bg-[#1A1A1E]">
                      {n} {n === 1 ? "Guest" : "Guests"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block font-mono-accent text-[11px] uppercase tracking-widest text-zinc-500">
                  Date
                </label>
                <input
                  data-testid="reservation-form-date-input"
                  required
                  type="date"
                  value={form.date}
                  onChange={set("date")}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
              <div>
                <label className="mb-1.5 block font-mono-accent text-[11px] uppercase tracking-widest text-zinc-500">
                  Time
                </label>
                <select
                  data-testid="reservation-form-time-select"
                  required
                  value={form.time}
                  onChange={set("time")}
                  className={`${inputCls} appearance-none`}
                >
                  <option value="" disabled className="bg-[#1A1A1E]">
                    Select a slot
                  </option>
                  {TIME_SLOTS.map((t) => (
                    <option key={t} value={t} className="bg-[#1A1A1E]">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block font-mono-accent text-[11px] uppercase tracking-widest text-zinc-500">
                  Special Requests
                </label>
                <input
                  data-testid="reservation-form-notes-input"
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Birthday, window seat..."
                  className={inputCls}
                />
              </div>
              <button
                data-testid="reservation-form-submit-button"
                type="submit"
                disabled={submitting}
                className="mt-2 rounded-full bg-[#F57C00] px-7 py-3.5 font-grotesk text-sm font-semibold text-black transition-all duration-300 hover:bg-[#FF9800] hover:shadow-[0_0_36px_rgba(245,124,0,0.5)] disabled:opacity-60 sm:col-span-2"
              >
                {submitting ? "Sending..." : "Request a Table"}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15} className="flex h-full flex-col gap-5">
            <div className="card-surface rounded-3xl p-7">
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-[#F57C00]" />
                <h3 className="font-grotesk text-lg font-semibold">Opening Hours</h3>
              </div>
              <div className="mt-4 flex items-center justify-between border-b border-white/8 pb-3 text-sm">
                <span className="text-zinc-400">Monday – Sunday</span>
                <span data-testid="hours-value" className="font-mono-accent font-semibold text-[#FFB74D]">
                  {INFO.hours}
                </span>
              </div>
              <p className="mt-3 text-sm text-[#4CAF50]">{INFO.days}</p>
            </div>

            <div className="card-surface rounded-3xl p-7">
              <div className="flex items-center gap-3">
                <MapPin size={18} className="text-[#F57C00]" />
                <h3 className="font-grotesk text-lg font-semibold">Find Us</h3>
              </div>
              <p data-testid="address-value" className="mt-4 text-sm leading-relaxed text-zinc-400">
                {INFO.address}
              </p>
              <iframe
                data-testid="map-embed"
                title="Podi Sambaram location map"
                src="https://www.google.com/maps?q=28.285473,77.067986&z=16&output=embed"
                className="mt-5 h-52 w-full rounded-2xl border border-white/10"
                loading="lazy"
              />
              <a
                data-testid="directions-link"
                href={INFO.maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#F57C00]/40 px-5 py-2.5 font-grotesk text-sm font-medium text-[#FF9800] transition-all duration-300 hover:bg-[#F57C00] hover:text-black"
              >
                <Navigation size={14} /> Get Directions
              </a>
            </div>

            <div className="card-surface rounded-3xl p-7">
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-[#F57C00]" />
                <h3 className="font-grotesk text-lg font-semibold">Call or Order In</h3>
              </div>
              <a
                data-testid="phone-link"
                href={`tel:${INFO.phone.replace(/\s/g, "")}`}
                className="mt-4 block font-mono-accent text-xl font-semibold text-[#F5F2EB] transition-colors hover:text-[#FF9800]"
              >
                {INFO.phone}
              </a>
              <a
                data-testid="swiggy-order-link"
                href={INFO.swiggy_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-[#FF9800]"
              >
                Order delivery on Swiggy <ExternalLink size={13} />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Reservation;
